import { createSyncRoomWs } from '@/utils/syncRoomWs'

let ws = null
let reconnectTimer = null
let heartbeatTimer = null

function decodeBase64Url(str) {
  const base64 = str.replace(/-/g, '+').replace(/_/g, '/')
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  return window.atob(base64 + padding)
}

function parseJwtPayload(token) {
  if (!token) return null
  const raw = token.toLowerCase().startsWith('jwt ') ? token.slice(4) : token
  const parts = raw.split('.')
  if (parts.length !== 3) return null
  try {
    const payload = JSON.parse(decodeBase64Url(parts[1]))
    return payload
  } catch (e) {
    return null
  }
}

function getCookie(name) {
  const cookies = document.cookie.split(';')
  for (let i = 0; i < cookies.length; i += 1) {
    const cookie = cookies[i].trim()
    if (cookie.startsWith(`${name}=`)) {
      return decodeURIComponent(cookie.substring(name.length + 1))
    }
  }
  return ''
}

function getCurrentUserIdFromToken() {
  const token = getCookie('AUTHORIZATION')
  const payload = parseJwtPayload(token)
  return payload && payload.user_id ? payload.user_id : null
}

function getCurrentUsernameFromCookie() {
  return getCookie('username') || ''
}

function isSameUserId(left, right) {
  if (left === null || left === undefined || right === null || right === undefined) {
    return false
  }
  return String(left) === String(right)
}

const PLAYBACK_ACTION_TEXT = {
  play: '开始播放',
  pause: '暂停播放',
  seek: '调整进度',
  next: '切到下一首',
  prev: '切到上一首',
  switch_track: '切换歌曲',
}

const syncRoom = {
  namespaced: true,
  state: {
    roomId: '',
    ownerUserId: null,
    onlineCount: 0,
    onlineUsers: [],
    ownerOnline: true,
    currentUserId: null,
    wsConnected: false,
    isJoined: false,
    dismissed: false,
    applyingRemoteState: false,
    lastChatMessage: null,
    playbackNoticeVersion: 0,
    playbackState: {
      track_id: null,
      is_playing: false,
      position_ms: 0,
      version: 0,
      updated_at_ms: 0,
      operator_user_id: null,
    },
  },
  mutations: {
    setRoom(state, room) {
      state.roomId = room.room_id || ''
      state.ownerUserId = room.owner_user_id || null
      state.onlineCount = room.online_count || 0
      state.onlineUsers = room.online_users || []
      state.ownerOnline = room.owner_online !== false
      state.currentUserId = getCurrentUserIdFromToken()
      if (!state.currentUserId) {
        const username = getCurrentUsernameFromCookie()
        const users = Array.isArray(room.online_users) ? room.online_users : []
        const currentUser = users.find((item) => item.username === username)
        state.currentUserId = currentUser ? currentUser.user_id : null
      }
      state.isJoined = true
      state.dismissed = false
    },
    setPlaybackState(state, playbackState) {
      if (!playbackState) return
      if (playbackState.version < state.playbackState.version) {
        return
      }
      state.playbackState = {
        ...state.playbackState,
        ...playbackState,
      }
    },
    setOnlineCount(state, count) {
      state.onlineCount = count
    },
    setOnlineUsers(state, users) {
      state.onlineUsers = users || []
    },
    setOwnerOnline(state, value) {
      state.ownerOnline = value !== false
    },
    setLastChatMessage(state, message) {
      state.lastChatMessage = message || null
    },
    setPlaybackNoticeVersion(state, version) {
      state.playbackNoticeVersion = Number(version || 0)
    },
    setWsConnected(state, connected) {
      state.wsConnected = connected
    },
    setDismissed(state, value) {
      state.dismissed = value
      if (value) {
        state.isJoined = false
      }
    },
    setApplyingRemoteState(state, value) {
      state.applyingRemoteState = value
    },
    reset(state) {
      state.roomId = ''
      state.ownerUserId = null
      state.onlineCount = 0
      state.onlineUsers = []
      state.ownerOnline = true
      state.currentUserId = getCurrentUserIdFromToken()
      state.wsConnected = false
      state.isJoined = false
      state.dismissed = false
      state.applyingRemoteState = false
      state.lastChatMessage = null
      state.playbackNoticeVersion = 0
      state.playbackState = {
        track_id: null,
        is_playing: false,
        position_ms: 0,
        version: 0,
        updated_at_ms: 0,
        operator_user_id: null,
      }
    },
  },
  actions: {
    async createRoom({ dispatch }, payload = {}) {
      const res = await this._vm.$api.Task.syncRoomCreate(payload)
      if (!res.result) {
        throw new Error(res.message || '创建房间失败')
      }
      await dispatch('consumeRoomSnapshot', res.data)
      return res.data
    },
    async joinRoom({ dispatch }, roomId) {
      const res = await this._vm.$api.Task.syncRoomJoin({ room_id: roomId })
      if (!res.result) {
        throw new Error(res.message || '加入房间失败')
      }
      await dispatch('consumeRoomSnapshot', res.data)
      return res.data
    },
    async leaveRoom({ state, commit }) {
      if (!state.roomId) return
      await this._vm.$api.Task.syncRoomLeave({ room_id: state.roomId })
      if (ws) {
        ws.close()
        ws = null
      }
      if (reconnectTimer) {
        clearTimeout(reconnectTimer)
        reconnectTimer = null
      }
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer)
        heartbeatTimer = null
      }
      commit('reset')
    },
    async consumeRoomSnapshot({ state, commit, dispatch }, data) {
      commit('setRoom', data.room)
      commit('setPlaybackState', data.playback_state)

      const isOwner = isSameUserId(state.currentUserId, state.ownerUserId)
      if (!isOwner) {
        await dispatch('applyPlaybackToPlayer', data.playback_state || {})
      }

      dispatch('connectWs')

      if (isOwner) {
        await dispatch('syncCurrentPlaybackState')
      }
    },
    connectWs({ state, commit, dispatch }) {
      if (!state.roomId) return
      if (ws) {
        ws.close()
        ws = null
      }

      ws = createSyncRoomWs(state.roomId, {
        onOpen: () => {
          commit('setWsConnected', true)
          if (heartbeatTimer) {
            clearInterval(heartbeatTimer)
          }
          heartbeatTimer = setInterval(() => {
            dispatch('heartbeat')
          }, 15000)
        },
        onClose: (evt) => {
          commit('setWsConnected', false)
          if (heartbeatTimer) {
            clearInterval(heartbeatTimer)
            heartbeatTimer = null
          }
          if (!state.isJoined) return

          const closeCode = evt && Number(evt.code || 0)
          if (closeCode === 4403) {
            dispatch('forceDismiss', { reason: 'owner_timeout' })
            return
          }

          reconnectTimer = setTimeout(() => {
            dispatch('connectWs')
          }, 1500)
        },
        onMessage: (msg) => {
          dispatch('handleWsMessage', msg)
        },
      })
    },
    handleWsMessage({ state, commit, dispatch }, msg) {
      if (!msg || !msg.event) return
      if (msg.event === 'playback.state') {
        const prevState = state.playbackState || {}
        const payload = msg.data || {}
        commit('setPlaybackState', payload)

        const version = Number(payload.version || 0)
        const prevVersion = Number(prevState.version || 0)
        let actionType = payload.action_type || ''

        // Fallback inference for older backend payloads without action_type.
        if (!actionType && version > prevVersion) {
          if (prevState.track_id !== payload.track_id && payload.track_id) {
            actionType = 'switch_track'
          } else if (prevState.is_playing !== payload.is_playing) {
            actionType = payload.is_playing ? 'play' : 'pause'
          } else {
            const prevPos = Number(prevState.position_ms || 0)
            const nextPos = Number(payload.position_ms || 0)
            if (Math.abs(nextPos - prevPos) > 3000) {
              actionType = 'seek'
            }
          }
        }

        if (actionType && version > Number(state.playbackNoticeVersion || 0)) {
          commit('setPlaybackNoticeVersion', version)
          const operatorFromMembers = (state.onlineUsers || []).find((u) => String(u.user_id) === String(payload.operator_user_id || ''))
          const operatorName = payload.operator_username || (operatorFromMembers ? operatorFromMembers.username : '') || '房主'
          const actionText = PLAYBACK_ACTION_TEXT[actionType] || '更新播放状态'
          const songSuffix = payload.track_name ? `《${payload.track_name}》` : ''
          const message = payload.queue_changed
            ? `${operatorName} ${actionText}${songSuffix}，更新播放列表`
            : `${operatorName} ${actionText}${songSuffix}`
          commit('setLastChatMessage', {
            room_id: state.roomId,
            type: 'system',
            event: 'playback_action',
            action_type: actionType,
            user_id: payload.operator_user_id || null,
            username: operatorName,
            message,
            version,
            created_at_ms: payload.updated_at_ms || Date.now(),
          })
        }

        dispatch('applyPlaybackToPlayer', payload)
      }
      if (msg.event === 'room.members') {
        const data = msg.data || {}
        commit('setOnlineCount', data.online_count || 0)
        commit('setOnlineUsers', data.online_users || [])
        commit('setOwnerOnline', data.owner_online !== false)
      }
      if (msg.event === 'room.chat') {
        const data = msg.data || {}
        if (data.type === 'system' && data.event === 'playback_action') {
          const version = Number(data.version || 0)
          if (version && version <= Number(state.playbackNoticeVersion || 0)) {
            return
          }
          if (version) {
            commit('setPlaybackNoticeVersion', version)
          }
        }
        commit('setLastChatMessage', data)
      }
      if (msg.event === 'room.dismissed') {
        dispatch('forceDismiss', { reason: msg.data && msg.data.reason })
      }
    },
    async applyPlaybackToPlayer({ rootState, commit, dispatch }, playbackState) {
      commit('setApplyingRemoteState', true)
      try {
        const roomTrackId = playbackState.track_id
        const remoteQueue = Array.isArray(playbackState.queue) ? playbackState.queue : []
        const hasRemoteQueue = remoteQueue.length > 0

        const findTrackIndex = (tracks, trackId) => {
          if (!trackId) return -1
          return tracks.findIndex((item) => String(item.id) === String(trackId))
        }

        let localQueue = Array.isArray(rootState.common.queue) ? rootState.common.queue : []

        if (hasRemoteQueue) {
          const rawQueueIndex = Number.isInteger(playbackState.queue_index) ? playbackState.queue_index : -1
          const byTrackIndex = findTrackIndex(remoteQueue, roomTrackId)
          const targetIndex = rawQueueIndex >= 0 && rawQueueIndex < remoteQueue.length
            ? rawQueueIndex
            : (byTrackIndex >= 0 ? byTrackIndex : 0)

          const shouldReplaceQueue = localQueue.length !== remoteQueue.length
            || localQueue.some((item, idx) => String(item.id) !== String(remoteQueue[idx].id))

          if (shouldReplaceQueue || rootState.common.queueIndex !== targetIndex) {
            await dispatch('playTrackList', { tracks: remoteQueue, index: targetIndex }, { root: true })
          }
          localQueue = remoteQueue
        }

        if (!hasRemoteQueue && roomTrackId) {
          const index = findTrackIndex(localQueue, roomTrackId)
          if (index >= 0 && rootState.common.queueIndex !== index) {
            await dispatch('playTrackList', { tracks: localQueue, index }, { root: true })
          }
        }

        const duration = rootState.common.duration || 0
        if (duration > 0) {
          const localPositionMs = Math.floor((rootState.common.currentTime || 0) * 1000)
          const drift = Math.abs(localPositionMs - (playbackState.position_ms || 0))
          if (drift > 500) {
            const progress = Math.min(1, Math.max(0, (playbackState.position_ms || 0) / (duration * 1000)))
            await dispatch('seek', progress, { root: true })
          }
        }

        if (playbackState.is_playing && !rootState.common.isPlaying) {
          await dispatch('resume', null, { root: true })
        }
        if (!playbackState.is_playing && rootState.common.isPlaying) {
          await dispatch('pause', null, { root: true })
        }
      } finally {
        commit('setApplyingRemoteState', false)
      }
    },
    forceDismiss({ commit }, payload = {}) {
      if (ws) {
        ws.close()
        ws = null
      }
      if (reconnectTimer) {
        clearTimeout(reconnectTimer)
        reconnectTimer = null
      }
      if (heartbeatTimer) {
        clearInterval(heartbeatTimer)
        heartbeatTimer = null
      }
      commit('reset')
      commit('setDismissed', true)
      if (this._vm && this._vm.$myMsg) {
        const reason = payload.reason || 'owner_left'
        const content = reason === 'owner_timeout'
          ? '房主掉线超时，在线会话已结束'
          : '房主已离开，在线会话已结束'
        this._vm.$myMsg.notify({
          title: '房间已解散',
          content,
          type: 'warning',
          time: 2000,
        })
      }
    },
    async control({ state, getters }, payload) {
      if (!state.roomId) {
        throw new Error('未加入房间')
      }
      if (!getters.isOwner) {
        throw new Error('仅房主可控制播放')
      }
      const res = await this._vm.$api.Task.syncRoomControl({
        room_id: state.roomId,
        ...payload,
      })
      if (!res.result) {
        throw new Error(res.message || '操作失败')
      }
      return res.data
    },
    async heartbeat({ state, commit }) {
      if (!state.roomId) return
      const res = await this._vm.$api.Task.syncRoomHeartbeat({ room_id: state.roomId })
      if (res && res.result && res.data) {
        if (typeof res.data.online_count === 'number') {
          commit('setOnlineCount', res.data.online_count)
        }
        if (res.data.online_users) {
          commit('setOnlineUsers', res.data.online_users)
        }
        commit('setOwnerOnline', res.data.owner_online !== false)
      }
    },
    async sendChat({ state }, message) {
      if (!state.roomId) {
        throw new Error('未加入房间')
      }
      const text = (message || '').trim()
      if (!text) {
        throw new Error('消息不能为空')
      }
      const res = await this._vm.$api.Task.syncRoomChat({
        room_id: state.roomId,
        message: text,
      })
      if (!res.result) {
        throw new Error(res.message || '消息发送失败')
      }
      return res.data
    },
    async syncCurrentPlaybackState({ state, getters, rootState, dispatch }) {
      if (!state.roomId || !getters.isOwner) return

      const common = rootState.common || {}
      const queue = Array.isArray(common.queue) ? common.queue : []
      const queueIndex = Number.isInteger(common.queueIndex) ? common.queueIndex : -1
      const track = queueIndex >= 0 && queueIndex < queue.length ? queue[queueIndex] : null

      await dispatch('control', {
        type: common.isPlaying ? 'play' : 'pause',
        track_id: track ? track.id : null,
        queue,
        queue_index: queueIndex,
        position_ms: Math.floor((common.currentTime || 0) * 1000),
      })
    },
  },
  getters: {
    isOwner(state) {
      return isSameUserId(state.currentUserId, state.ownerUserId)
    },
  },
}

export default syncRoom
